import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject } from '@angular/core';
import { Observable, catchError, of, forkJoin, map } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DmsLinkEntityType, EntityLookupItem, EntityLookupResponse } from './entity-lookup.types';

/** Ein Cross-Typ-Suchtreffer: Item + sein Typ (für den Projekt-Baum-Such-Merge). */
export interface TypedLookupHit {
  readonly type: DmsLinkEntityType;
  readonly item: EntityLookupItem;
}

const ENDPOINT = '/dms/entity-lookup';
const CHUNK_SIZE = 50;

/**
 * EntityLookupService — sucht und resolved Entitäten via DMS-Lookup-Endpoint.
 *
 * Features:
 * - `search()`: Volltextsuche für Autocomplete (GET mit ?type&q&limit).
 * - `resolve()`: Batch-Auflösung von IDs (GET mit ?type&ids[]=...), gecacht.
 * - Chunking: IDs werden in Batches von max. CHUNK_SIZE aufgeteilt.
 * - Cache: aufgelöste Items werden in einer Map gehalten (type:id → item).
 * - `clearCache()`: wird bei Mandant-Wechsel aufgerufen.
 */
@Injectable({ providedIn: 'root' })
export class EntityLookupService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, EntityLookupItem>();

  constructor() {
    // Bei Mandant-Wechsel Cache leeren, damit keine Daten eines anderen
    // Mandanten im Speicher verbleiben.
    const auth = inject(AuthService);
    effect(() => {
      const _mandantId = auth.activeMandantId();
      this.cache.clear();
    });
  }

  /**
   * Sucht Entitäten des gegebenen Typs nach Freitext.
   * Ergebnisse werden im Cache abgelegt für späteres resolve().
   */
  search(type: DmsLinkEntityType, q: string | null, limit = 20): Observable<readonly EntityLookupItem[]> {
    let params = new HttpParams().set('type', type).set('limit', String(limit));
    if (q !== null && q.trim() !== '') {
      params = params.set('q', q.trim());
    }
    return this.http.get<EntityLookupResponse>(ENDPOINT, { params }).pipe(
      map((res) => {
        for (const item of res.items) {
          this.cache.set(this.cacheKey(type, item.id), item);
        }
        return res.items;
      }),
    );
  }

  /**
   * Löst eine Liste von Entity-IDs zu EntityLookupItems auf.
   * Gecachte Items werden direkt geliefert; fehlende werden in Chunks
   * (max. CHUNK_SIZE) via parallele GET-Requests nachgeladen.
   */
  resolve(type: DmsLinkEntityType, ids: readonly string[]): Observable<readonly EntityLookupItem[]> {
    if (ids.length === 0) {
      return of([]);
    }

    const fromCache: EntityLookupItem[] = [];
    const toFetch: string[] = [];
    for (const id of ids) {
      const cached = this.cache.get(this.cacheKey(type, id));
      if (cached) {
        fromCache.push(cached);
      } else {
        toFetch.push(id);
      }
    }
    if (toFetch.length === 0) {
      return of(fromCache);
    }

    const chunks: string[][] = [];
    for (let i = 0; i < toFetch.length; i += CHUNK_SIZE) {
      chunks.push(toFetch.slice(i, i + CHUNK_SIZE));
    }

    const requests = chunks.map((chunk) => {
      let params = new HttpParams().set('type', type);
      for (const id of chunk) {
        // Symfony `$request->query->all('ids')` erwartet Array-Notation `ids[]=…`.
        // Ein skalares `ids=…` wird als String interpretiert und löst eine
        // BadRequestException (HTTP 400 „expecting array, got string") aus.
        params = params.append('ids[]', id);
      }
      return this.http.get<EntityLookupResponse>(ENDPOINT, { params }).pipe(
        map((res) => {
          for (const item of res.items) {
            this.cache.set(this.cacheKey(type, item.id), item);
          }
          return res.items;
        }),
      );
    });

    return forkJoin(requests).pipe(map((parts) => [...fromCache, ...parts.flat()]));
  }

  /**
   * Listet die direkten Kinder von `type` unter `parentId` (Lazy-Drilldown).
   * Optionales `q` filtert innerhalb der Kinder. Treffer werden gecacht.
   */
  listChildren(
    parentType: DmsLinkEntityType,
    parentId: string,
    type: DmsLinkEntityType,
    q?: string | null,
    limit = 50,
  ): Observable<readonly EntityLookupItem[]> {
    let params = new HttpParams()
      .set('type', type)
      .set('parentType', parentType)
      .set('parentId', parentId)
      .set('limit', String(limit));
    if (q !== null && q !== undefined && q.trim() !== '') {
      params = params.set('q', q.trim());
    }
    return this.http.get<EntityLookupResponse>(ENDPOINT, { params }).pipe(
      map((res) => {
        for (const item of res.items) {
          this.cache.set(this.cacheKey(type, item.id), item);
        }
        return res.items;
      }),
    );
  }

  /**
   * Cross-Typ-Suche für den Projekt-Baum-Tab: parallele search()-Calls je Typ,
   * Frontend-Merge zu typisierten Treffern. Teil-Fehler einzelner Typen werden
   * verschluckt (übrige Treffer bleiben sichtbar — Spec §9).
   */
  searchAcross(
    types: readonly DmsLinkEntityType[],
    q: string | null,
    limit = 20,
  ): Observable<readonly TypedLookupHit[]> {
    if (types.length === 0) {
      return of([]);
    }
    const calls = types.map((type) =>
      this.search(type, q, limit).pipe(
        map((items) => items.map((item) => ({ type, item }) satisfies TypedLookupHit)),
        catchError(() => of([] as TypedLookupHit[])),
      ),
    );
    return forkJoin(calls).pipe(map((parts) => parts.flat()));
  }

  /** Leert den vollständigen Cache. Beim Mandant-Wechsel aufrufen. */
  clearCache(): void {
    this.cache.clear();
  }

  private cacheKey(type: DmsLinkEntityType, id: string): string {
    return `${type}:${id}`;
  }
}
